import { useEffect, useMemo, useState } from "react"
import { createPortal } from "react-dom"

import { useLazyGetReordersListQuery } from "store/api/adminApi"
import { UITypes } from "types"
import { ReactComponent as ExportIcon } from "assets/svg/export-icon.svg"
import { ReactComponent as IconThermostat } from "assets/svg/thermostat.svg"

import useExportCSV from "hooks/useExportCSV"
import { ModalLayout } from "layouts/ModalLayout"
import { ModalHeader } from "components/common/ModalHeader"

import { convertReordersToTableRows, getReordersTableHeaderCells } from "./reordersListModalUtils"
import {
  ExportButtonWrapper,
  FooterButtonsWrapper,
  IconWrapper,
  ModalContent,
  ReordersModalStyle,
  StyledButton,
  StyledTable,
  TableWrapper,
} from "./styled"

interface ReordersListModalProps {
  selectedDateRange: [string, string]
  onClose: () => void
}

const ReordersListModal = ({ selectedDateRange, onClose }: ReordersListModalProps) => {
  const [triggerGetReordersList, { isFetching, data }] = useLazyGetReordersListQuery()
  const [contractorSearchValue, setContractorSearchValue] = useState("")
  const [deviceTypeFilter, setDeviceTypeFilter] = useState<UITypes.Option | null>(null)

  useEffect(() => {
    const fetchData = async () => {
      try {
        await triggerGetReordersList(
          {
            start_date: selectedDateRange[0],
            end_date: selectedDateRange[1],
          },
          true,
        )
      } catch (error) {
        console.error(error)
      }
    }

    fetchData()
  }, [selectedDateRange, triggerGetReordersList])

  const reordersData = data?.data || []

  const handleContractorSearchChange = (value: string) => {
    setContractorSearchValue(value)
  }

  const handleDeviceTypeChange = (option: UITypes.Option | null) => {
    setDeviceTypeFilter(option)
  }

  const tableHeaderCells: UITypes.TableHeaderCell[] = useMemo(
    () =>
      getReordersTableHeaderCells({
        onContractorSearchChange: handleContractorSearchChange,
        onDeviceTypeChange: handleDeviceTypeChange,
      }),
    [],
  )

  const { handleLocalExport } = useExportCSV({
    params: { start_date: selectedDateRange[0], end_date: selectedDateRange[1] },
    headerCells: tableHeaderCells,
    fileName: "reorders",
  })

  const filteredData = useMemo(() => {
    let filtered = reordersData

    if (contractorSearchValue) {
      filtered = filtered.filter(item =>
        item.contractor_name.toLowerCase().includes(contractorSearchValue.toLowerCase()),
      )
    }

    if (deviceTypeFilter && deviceTypeFilter.value) {
      filtered = filtered.filter(item => item.product_type === deviceTypeFilter.value)
    }

    return filtered
  }, [reordersData, contractorSearchValue, deviceTypeFilter])

  const tableData = useMemo(() => {
    return convertReordersToTableRows(filteredData)
  }, [filteredData])

  return (
    <>
      <ReordersModalStyle />
      {createPortal(
        <ModalLayout
          width="870px"
          isFetching={isFetching}
          onClose={onClose}
          className="reorders-modal"
          footerClassName="reorders-modal-footer"
          customButtons={
            <FooterButtonsWrapper>
              <StyledButton color="greyBordered" onClick={onClose}>
                Close
              </StyledButton>
              <StyledButton
                color="blue"
                onClick={() => handleLocalExport(tableData)}
                disabled={filteredData.length === 0}
              >
                <ExportButtonWrapper>
                  <ExportIcon />
                  <span>Export Data</span>
                </ExportButtonWrapper>
              </StyledButton>
            </FooterButtonsWrapper>
          }
        >
          <ModalHeader title="Reorders" subtitle="Only Standard order types are considered here">
            <IconWrapper>
              <IconThermostat />
            </IconWrapper>
          </ModalHeader>
          <ModalContent>
            <TableWrapper>
              <StyledTable headerCells={tableHeaderCells} data={tableData} isLoading={isFetching} />
            </TableWrapper>
          </ModalContent>
        </ModalLayout>,
        document.body,
      )}
    </>
  )
}

export default ReordersListModal
